import { Headshot } from './Marks.jsx'
import { surname } from './model.js'

// The five on the floor, on a live card and in the game view. P6-330b (GN-3b).
//
// Both are drawn from `funModel.js`'s answer (`cardFloor`, `gameFloor`): `{ away: [...], home: [...] }`, each side
// EXACTLY as long as the feed's flags made it. Neither of them says "five" anywhere and neither pads, trims or
// assumes: a side with four is drawn with four, a side with none is drawn as a side with none (`none listed`, in
// words, because a row with a tricode and nothing after it reads as a missing picture), and the whole thing is not
// drawn at all when nobody is on either side (`funModel.js`).
//
// ═══════════════════════════════════════════════════════════════════════════
// A FACE IS DECORATION AND A NAME IS NOT
// ═══════════════════════════════════════════════════════════════════════════
//
// `Marks.jsx` draws every face `aria-hidden` over its initials, because on the box score the name is written beside
// it. Here the face IS the whole mark on a card, so each one carries the player's name for a screen reader (visually
// hidden) and as a hover title, and the list is named for its team. `teamOf` is the board's table entry for each
// side, as everywhere else, and `sides` is `{ away: { tricode, name }, home: { tricode, name } }`.

const figure = (value) => (Number.isInteger(value) ? String(value) : '–')

function None() {
  return (
    <span className="d2d-hoops__floornone d2d-micro" data-testid="hoops-floor-none">
      none listed
    </span>
  )
}

/** A live card's five on the floor: a row per side, the tricode and the faces (28px). From 720px; `GameCard` decides. */
export function CardFloor({ floor, sides }) {
  return (
    <div className="d2d-hoops__floor" data-testid="hoops-card-floor" role="group" aria-label="On the floor">
      <span className="d2d-hoops__rowlabel d2d-micro" aria-hidden="true">
        On the floor
      </span>
      {['away', 'home'].map((which) => (
        <div className="d2d-hoops__floorrow" key={which} data-side={which} data-testid={`hoops-card-floor-${which}`}>
          <span className="d2d-hoops__floorabbr" data-side={which}>
            {sides[which].tricode}
          </span>
          {floor[which].length === 0 ? (
            <None />
          ) : (
            <ul className="d2d-hoops__floorfaces" aria-label={`${sides[which].tricode} on the floor`}>
              {floor[which].map((player, index) => (
                <li
                  className="d2d-hoops__floorface"
                  key={player.person_id ?? `${player.name}-${index}`}
                  title={player.name}
                  data-testid="hoops-card-floor-player"
                >
                  <Headshot player={player} which={which} size="small" />
                  <span className="d2d-sr-only">{player.name}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  )
}

/** The game view's section: both teams, and under each face the surname and the points and fouls. */
export function GameFloor({ floor, sides }) {
  return (
    <section className="d2d-hoops__panel" aria-label="On the floor" data-testid="hoops-floor">
      <h3 className="d2d-hoops__sectiontitle d2d-micro">On the floor</h3>
      <div className="d2d-hoops__floorgrid">
        {['away', 'home'].map((which) => (
          <div className="d2d-hoops__floorteam" key={which} data-side={which} data-testid={`hoops-floor-${which}`}>
            <span className="d2d-hoops__floorteamname d2d-micro" data-side={which}>
              {sides[which].name ?? sides[which].tricode}
            </span>
            {floor[which].length === 0 ? (
              <None />
            ) : (
              <ul className="d2d-hoops__floorplayers" aria-label={`${sides[which].name ?? sides[which].tricode} on the floor`}>
                {floor[which].map((player, index) => (
                  <li
                    className="d2d-hoops__floorplayer"
                    key={player.person_id ?? `${player.name}-${index}`}
                    title={player.name}
                    data-testid="hoops-floor-player"
                  >
                    <Headshot player={player} which={which} />
                    <span className="d2d-hoops__floorname">{surname(player.name)}</span>
                    <span className="d2d-hoops__floorstat d2d-micro">
                      <span>{figure(player.points)} PTS</span>
                      <span>{figure(player.fouls)} PF</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </section>
  )
}
